import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { Institution } from '../types';

const TIER_COLORS: Record<string, string> = {
  elite:   '#014c8d',
  pro:     '#0fa86d',
  starter: '#e88c0e',
};

const TIER_DEFAULTS: Record<string, { fee: number; maxAthletes: number | null }> = {
  starter: { fee: 7500,  maxAthletes: 15 },
  pro:     { fee: 10000, maxAthletes: 30 },
  elite:   { fee: 15000, maxAthletes: null }, // null = unlimited
};

const INST_TYPES = [
  'High School', 'Prep School', 'AAU Program',
  'College', 'University', 'Professional Team',
  'Sports Organization', 'Player Association', 'Booster Club',
  'Agent / Player Management', 'Corporation', 'Wealth Management Firm',
  'Other',
];

function formatDate(ts: string | null): string {
  if (!ts) return '—';
  return new Date(ts).toLocaleDateString('en-CA', { month: 'short', day: 'numeric', year: 'numeric' });
}

function addDays(dateStr: string, days: number): string {
  const d = new Date(dateStr);
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

function daysRemaining(endDate: string | null): number | null {
  if (!endDate) return null;
  return Math.ceil((new Date(endDate).getTime() - Date.now()) / 86400000);
}

function InstitutionCard({ inst, onClick }: { inst: Institution; onClick: () => void }) {
  const tierColor = TIER_COLORS[inst.license_tier?.toLowerCase() || ''] || '#4a6080';
  const days = daysRemaining(inst.license_end_date);

  return (
    <div
      onClick={onClick}
      style={{
        background: 'var(--surface)', borderRadius: 12, border: '1px solid var(--border)',
        padding: '18px', position: 'relative', overflow: 'hidden',
        cursor: 'pointer', transition: 'border-color 0.15s',
      }}
      onMouseEnter={e => { (e.currentTarget as HTMLDivElement).style.borderColor = tierColor + '66'; }}
      onMouseLeave={e => { (e.currentTarget as HTMLDivElement).style.borderColor = 'var(--border)'; }}
    >
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: tierColor }} />

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
        <div>
          <div style={{ fontFamily: 'Barlow Condensed, sans-serif', fontWeight: 800, fontSize: 18, color: 'var(--text)', lineHeight: 1.1 }}>{inst.name}</div>
          <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 3 }}>{inst.type}{inst.state_province ? ` · ${inst.state_province},` : ''} {inst.country}</div>
        </div>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexShrink: 0 }}>
          {inst.license_tier && (
            <span style={{ padding: '3px 8px', borderRadius: 12, background: tierColor + '22', color: tierColor, fontSize: 10, fontWeight: 700, textTransform: 'uppercase' }}>
              {inst.license_tier}
            </span>
          )}
          <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 600, color: inst.status === 'active' ? '#0fa86d' : inst.status === 'pending' ? '#e88c0e' : '#4a6080' }}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: inst.status === 'active' ? '#0fa86d' : inst.status === 'pending' ? '#e88c0e' : '#4a6080', display: 'inline-block' }} />
            {inst.status}
          </span>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginBottom: 12 }}>
        <div style={{ background: 'var(--surface-2)', borderRadius: 8, padding: '8px 10px' }}>
          <div style={{ fontSize: 9, color: 'var(--text-3)', fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 3 }}>Athletes</div>
          <div style={{ fontFamily: 'Barlow Condensed, sans-serif', fontWeight: 800, fontSize: 20, color: 'var(--text)' }}>
            {inst.athlete_count ?? 0}{inst.max_athletes ? `/${inst.max_athletes}` : ''}
          </div>
        </div>
        <div style={{ background: 'var(--surface-2)', borderRadius: 8, padding: '8px 10px' }}>
          <div style={{ fontSize: 9, color: 'var(--text-3)', fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 3 }}>Fee</div>
          <div style={{ fontFamily: 'Barlow Condensed, sans-serif', fontWeight: 800, fontSize: 20, color: 'var(--text)' }}>
            {inst.license_fee != null ? `$${inst.license_fee.toLocaleString()}` : '—'}
          </div>
        </div>
        <div style={{ background: 'var(--surface-2)', borderRadius: 8, padding: '8px 10px' }}>
          <div style={{ fontSize: 9, color: 'var(--text-3)', fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 3 }}>Days Left</div>
          <div style={{ fontFamily: 'Barlow Condensed, sans-serif', fontWeight: 800, fontSize: 20, color: days != null && days < 30 ? '#bf1e2e' : 'var(--text)' }}>
            {days != null ? days : '—'}
          </div>
        </div>
      </div>

      {(inst.contact_name || inst.contact_email) && (
        <div style={{ fontSize: 11, color: 'var(--text-3)', paddingTop: 10, borderTop: '1px solid var(--border)' }}>
          <span style={{ fontWeight: 600, color: 'var(--text-2)' }}>{inst.contact_name || 'Contact'}</span>
          {inst.contact_email && <span> · <a href={`mailto:${inst.contact_email}`} style={{ color: '#5b9bd5', textDecoration: 'none' }} onClick={e => e.stopPropagation()}>{inst.contact_email}</a></span>}
        </div>
      )}
      <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 4 }}>Added {formatDate(inst.created_at)}</div>
    </div>
  );
}

const TODAY = new Date().toISOString().slice(0, 10);

const emptyForm = {
  name: '', type: 'University', contact_name: '', contact_email: '',
  country: 'Canada', state_province: '', currency: 'CAD',
  license_tier: '', license_fee: '', license_start_date: TODAY,
  license_end_date: addDays(TODAY, 365), max_athletes: '',
  status: 'active', internal_notes: '',
};

export default function InstitutionsPage() {
  const [institutions, setInstitutions] = useState<Institution[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState<Institution | null>(null);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState<any>(null);
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState('');

  const load = () => {
    setLoading(true);
    supabase
      .from('institutions')
      .select('*')
      .order('created_at', { ascending: false })
      .then(async ({ data }) => {
        const insts = (data as Institution[]) ?? [];
        // Compute live athlete counts from student_athletes
        const ids = insts.map(i => i.id);
        if (ids.length > 0) {
          const [{ data: saCounts }, { data: epCounts }] = await Promise.all([
            supabase.from('student_athletes').select('institution_id').in('institution_id', ids),
            supabase.from('profiles').select('institution_id').in('institution_id', ids).eq('user_type', 'early_pathways'),
          ]);
          const countMap: Record<string, number> = {};
          (saCounts ?? []).forEach((r: any) => {
            countMap[r.institution_id] = (countMap[r.institution_id] || 0) + 1;
          });
          (epCounts ?? []).forEach((r: any) => {
            countMap[r.institution_id] = (countMap[r.institution_id] || 0) + 1;
          });
          setInstitutions(insts.map(i => ({ ...i, athlete_count: countMap[i.id] ?? 0 })));
          setLoading(false);
          return;
        }
        setInstitutions(insts);
        setLoading(false);
      });
  };

  useEffect(() => { load(); }, []);

  // Auto-populate fee and max athletes when tier changes
  const handleTierChange = (tier: string) => {
    const defaults = TIER_DEFAULTS[tier.toLowerCase()];
    setForm(f => ({
      ...f,
      license_tier: tier,
      license_fee: defaults ? String(defaults.fee) : f.license_fee,
      max_athletes: defaults ? (defaults.maxAthletes != null ? String(defaults.maxAthletes) : '') : f.max_athletes,
    }));
  };

  // Auto-calculate end date when start date changes
  const handleStartDateChange = (date: string) => {
    setForm(f => ({ ...f, license_start_date: date, license_end_date: addDays(date, 365) }));
  };

  const handleSave = async () => {
    if (!form.name.trim()) { setError('Institution name is required.'); return; }
    setSaving(true); setError('');
    const payload: any = {
      name: form.name.trim(),
      type: form.type,
      contact_name: form.contact_name || null,
      contact_email: form.contact_email || null,
      country: form.country || 'Canada',
      state_province: form.state_province || null,
      currency: form.currency || 'CAD',
      license_tier: form.license_tier || null,
      license_fee: form.license_fee ? parseFloat(form.license_fee) : null,
      license_start_date: form.license_start_date || null,
      license_end_date: form.license_end_date || null,
      max_athletes: form.max_athletes ? parseInt(form.max_athletes) : null,
      status: form.status,
      internal_notes: form.internal_notes || null,
    };
    const { error: err } = await supabase.from('institutions').insert(payload);
    setSaving(false);
    if (err) { setError(err.message); return; }
    setForm(emptyForm);
    setShowForm(false);
    load();
  };

  const openEdit = (inst: Institution) => {
    setEditForm({
      name: inst.name,
      type: inst.type,
      contact_name: inst.contact_name || '',
      contact_email: inst.contact_email || '',
      country: inst.country || 'Canada',
      state_province: inst.state_province || '',
      currency: inst.currency || 'CAD',
      license_tier: inst.license_tier || '',
      license_fee: inst.license_fee != null ? String(inst.license_fee) : '',
      license_start_date: inst.license_start_date || '',
      license_end_date: inst.license_end_date || '',
      max_athletes: inst.max_athletes != null ? String(inst.max_athletes) : '',
      status: inst.status,
      internal_notes: inst.internal_notes || '',
    });
    setEditError('');
    setEditing(true);
  };

  const handleEditSave = async () => {
    if (!selected) return;
    if (!editForm.name.trim()) { setEditError('Institution name is required.'); return; }
    setEditSaving(true); setEditError('');
    const payload: any = {
      name: editForm.name.trim(),
      type: editForm.type,
      contact_name: editForm.contact_name || null,
      contact_email: editForm.contact_email || null,
      country: editForm.country || 'Canada',
      state_province: editForm.state_province || null,
      currency: editForm.currency || 'CAD',
      license_tier: editForm.license_tier || null,
      license_fee: editForm.license_fee ? parseFloat(editForm.license_fee) : null,
      license_start_date: editForm.license_start_date || null,
      license_end_date: editForm.license_end_date || null,
      max_athletes: editForm.max_athletes ? parseInt(editForm.max_athletes) : null,
      status: editForm.status,
      internal_notes: editForm.internal_notes || null,
    };
    const { error: err } = await supabase.from('institutions').update(payload).eq('id', selected.id);
    setEditSaving(false);
    if (err) { setEditError(err.message); return; }
    setEditing(false);
    load();
    setSelected({ ...selected, ...payload });
  };

  const handleToggleSuspend = async () => {
    if (!selected) return;
    const newStatus = selected.status === 'active' ? 'suspended' : 'active';
    await supabase.from('institutions').update({ status: newStatus }).eq('id', selected.id);
    const updated = { ...selected, status: newStatus };
    setSelected(updated);
    setInstitutions(prev => prev.map(i => i.id === selected.id ? updated : i));
  };

  const handleRenew = async () => {
    if (!selected) return;
    const base = selected.license_end_date && new Date(selected.license_end_date) > new Date()
      ? selected.license_end_date
      : new Date().toISOString().slice(0, 10);
    const newEnd = addDays(base, 365);
    await supabase.from('institutions').update({ license_end_date: newEnd, status: 'active' }).eq('id', selected.id);
    const updated = { ...selected, license_end_date: newEnd, status: 'active' };
    setSelected(updated);
    setInstitutions(prev => prev.map(i => i.id === selected.id ? updated : i));
  };

  const inputStyle: React.CSSProperties = {
    width: '100%', background: 'var(--surface-2)', border: '1px solid var(--border)',
    color: 'var(--text)', borderRadius: 8, padding: '8px 11px', fontSize: 13, outline: 'none',
    fontFamily: 'Barlow, sans-serif', boxSizing: 'border-box',
  };
  const labelStyle: React.CSSProperties = {
    display: 'block', fontSize: 11, fontWeight: 700, color: 'var(--text-3)', letterSpacing: 1.2,
    textTransform: 'uppercase', marginBottom: 5,
  };

  const activeCount  = institutions.filter(i => i.status === 'active').length;
  const pendingCount = institutions.filter(i => i.status === 'pending').length;
  const expiredCount = institutions.filter(i => i.status === 'expired' || i.status === 'suspended').length;

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: 22, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
        <div>
          <div style={{ fontFamily: 'Barlow Condensed, sans-serif', fontWeight: 800, fontSize: 28, color: 'var(--text)' }}>Institutions</div>
          <div style={{ fontSize: 13, color: 'var(--text-3)', marginTop: 4, display: 'flex', gap: 14 }}>
            <span style={{ color: '#0fa86d', fontWeight: 600 }}>{activeCount} active</span>
            {pendingCount > 0 && <span style={{ color: '#e88c0e', fontWeight: 600 }}>{pendingCount} pending</span>}
            {expiredCount > 0 && <span style={{ color: '#4a6080', fontWeight: 600 }}>{expiredCount} expired</span>}
          </div>
        </div>
        <button className="btn btn-primary" onClick={() => setShowForm(v => !v)}>
          {showForm ? '✕ Cancel' : '+ Add Institution'}
        </button>
      </div>

      {/* Add form */}
      {showForm && (
        <div className="panel" style={{ marginBottom: 24 }}>
          <div className="panel-header"><div className="panel-title">New Institution</div></div>
          <div style={{ padding: '20px 24px' }}>

            {/* Row 1: Name + Type */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
              <div>
                <label style={labelStyle}>Institution Name *</label>
                <input style={inputStyle} value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. University of Waterloo" />
              </div>
              <div>
                <label style={labelStyle}>Type</label>
                <select style={{ ...inputStyle, cursor: 'pointer' }} value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value }))}>
                  {INST_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
            </div>

            {/* Row 2: Contact */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
              <div>
                <label style={labelStyle}>Contact Name</label>
                <input style={inputStyle} value={form.contact_name} onChange={e => setForm(f => ({ ...f, contact_name: e.target.value }))} placeholder="e.g. Jane Smith" />
              </div>
              <div>
                <label style={labelStyle}>Contact Email</label>
                <input style={inputStyle} type="email" value={form.contact_email} onChange={e => setForm(f => ({ ...f, contact_email: e.target.value }))} placeholder="e.g. jane@university.ca" />
              </div>
            </div>

            {/* Row 3: Location */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, marginBottom: 16 }}>
              <div>
                <label style={labelStyle}>Province / State</label>
                <input style={inputStyle} value={form.state_province} onChange={e => setForm(f => ({ ...f, state_province: e.target.value }))} placeholder="e.g. Ontario" />
              </div>
              <div>
                <label style={labelStyle}>Country</label>
                <select style={{ ...inputStyle, cursor: 'pointer' }} value={form.country} onChange={e => setForm(f => ({ ...f, country: e.target.value }))}>
                  <option value="Canada">Canada</option>
                  <option value="USA">USA</option>
                </select>
              </div>
              <div>
                <label style={labelStyle}>Currency</label>
                <select style={{ ...inputStyle, cursor: 'pointer' }} value={form.currency} onChange={e => setForm(f => ({ ...f, currency: e.target.value }))}>
                  <option value="CAD">CAD</option>
                  <option value="USD">USD</option>
                </select>
              </div>
            </div>

            {/* Row 4: License */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, marginBottom: 16 }}>
              <div>
                <label style={labelStyle}>License Tier</label>
                <select style={{ ...inputStyle, cursor: 'pointer' }} value={form.license_tier} onChange={e => handleTierChange(e.target.value)}>
                  <option value="">Select tier</option>
                  <option value="starter">Starter — $7,500</option>
                  <option value="pro">Pro — $10,000</option>
                  <option value="elite">Elite — $15,000</option>
                </select>
              </div>
              <div>
                <label style={labelStyle}>License Fee ({form.currency})</label>
                <input style={inputStyle} type="number" value={form.license_fee} onChange={e => setForm(f => ({ ...f, license_fee: e.target.value }))} placeholder="Auto-set by tier" />
              </div>
              <div>
                <label style={labelStyle}>Max Athletes</label>
                <input style={inputStyle} type="number" value={form.max_athletes} onChange={e => setForm(f => ({ ...f, max_athletes: e.target.value }))} placeholder="Auto-set by tier" />
              </div>
            </div>

            {/* Row 5: Dates + Status */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, marginBottom: 16 }}>
              <div>
                <label style={labelStyle}>License Start Date</label>
                <input style={inputStyle} type="date" value={form.license_start_date} onChange={e => handleStartDateChange(e.target.value)} />
              </div>
              <div>
                <label style={labelStyle}>License End Date</label>
                <input style={inputStyle} type="date" value={form.license_end_date} onChange={e => setForm(f => ({ ...f, license_end_date: e.target.value }))} />
              </div>
              <div>
                <label style={labelStyle}>Status</label>
                <select style={{ ...inputStyle, cursor: 'pointer' }} value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))}>
                  <option value="active">Active</option>
                  <option value="pending">Pending</option>
                  <option value="expired">Expired</option>
                  <option value="suspended">Suspended</option>
                </select>
              </div>
            </div>

            {/* Internal Notes */}
            <div style={{ marginBottom: 20 }}>
              <label style={labelStyle}>Internal Notes (BTHD only — never visible to institution)</label>
              <textarea
                style={{ ...inputStyle, height: 80, resize: 'vertical' }}
                value={form.internal_notes}
                onChange={e => setForm(f => ({ ...f, internal_notes: e.target.value }))}
                placeholder="Contract notes, special terms, relationship context..."
              />
            </div>

            {error && <div style={{ color: '#bf1e2e', fontSize: 12, marginBottom: 12 }}>{error}</div>}
            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : 'Save Institution'}
            </button>
          </div>
        </div>
      )}

      {/* Cards */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 60, color: 'var(--text-3)' }}>Loading institutions...</div>
      ) : institutions.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 60, color: 'var(--text-3)' }}>
          <div style={{ fontSize: 32, marginBottom: 12 }}>⬡</div>
          <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 6 }}>No institutions yet</div>
          <div style={{ fontSize: 13 }}>Add your first B2B client to start tracking licenses and athlete counts.</div>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 16 }}>
          {institutions.map(inst => (
            <InstitutionCard key={inst.id} inst={inst} onClick={() => setSelected(inst)} />
          ))}
        </div>
      )}

      {/* Institution Detail Panel */}
      {selected && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 200, display: 'flex', alignItems: 'stretch', justifyContent: 'flex-end' }}>
          <div onClick={() => { setSelected(null); setEditing(false); }} style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.5)' }} />
          <div style={{ position: 'relative', width: 460, background: 'var(--surface)', borderLeft: '1px solid var(--border)', overflowY: 'auto', zIndex: 1, display: 'flex', flexDirection: 'column' }}>

            {/* Panel header */}
            <div style={{ padding: '20px 24px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'flex-start', gap: 14 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontFamily: 'Barlow Condensed, sans-serif', fontWeight: 800, fontSize: 22, color: 'var(--text)', lineHeight: 1.1 }}>{selected.name}</div>
                <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 4 }}>{selected.type} · {selected.state_province ? `${selected.state_province}, ` : ''}{selected.country}</div>
              </div>
              <button onClick={() => { setSelected(null); setEditing(false); }} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--text-3)', fontSize: 20, padding: '4px 8px', borderRadius: 6 }}>✕</button>
            </div>

            <div style={{ padding: '20px 24px', flex: 1 }}>

              {/* Status + Tier badges */}
              <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
                {selected.license_tier && (
                  <span style={{ padding: '4px 10px', borderRadius: 20, background: (TIER_COLORS[selected.license_tier] || '#4a6080') + '22', color: TIER_COLORS[selected.license_tier] || '#4a6080', fontSize: 11, fontWeight: 700, textTransform: 'uppercase' }}>
                    {selected.license_tier}
                  </span>
                )}
                <span style={{ padding: '4px 10px', borderRadius: 20, background: selected.status === 'active' ? '#0fa86d22' : '#4a608022', color: selected.status === 'active' ? '#0fa86d' : '#4a6080', fontSize: 11, fontWeight: 700, textTransform: 'uppercase' }}>
                  {selected.status}
                </span>
              </div>

              {/* Fields */}
              {[
                { label: 'Contact', value: [selected.contact_name, selected.contact_email].filter(Boolean).join(' · ') || null },
                { label: 'Currency', value: selected.currency },
                { label: 'License Fee', value: selected.license_fee != null ? `$${selected.license_fee.toLocaleString()} ${selected.currency}` : null },
                { label: 'License Start', value: formatDate(selected.license_start_date) },
                { label: 'License End', value: formatDate(selected.license_end_date) },
                { label: 'Days Remaining', value: daysRemaining(selected.license_end_date) != null ? `${daysRemaining(selected.license_end_date)} days` : null },
                { label: 'Athletes Enrolled', value: selected.max_athletes ? `${selected.athlete_count ?? 0} / ${selected.max_athletes}` : String(selected.athlete_count ?? 0) },
                { label: 'Date Added', value: formatDate(selected.created_at) },
              ].map(({ label, value }) => (
                <div key={label} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', marginBottom: 12 }}>
                  <div style={{ fontSize: 11, color: 'var(--text-3)', fontWeight: 600, width: 130, flexShrink: 0, paddingTop: 1 }}>{label}</div>
                  <div style={{ fontSize: 13, color: value ? 'var(--text)' : 'var(--text-3)', fontStyle: value ? 'normal' : 'italic' }}>{value || 'Not set'}</div>
                </div>
              ))}

              {/* Internal Notes */}
              {selected.internal_notes && (
                <div style={{ marginTop: 8, marginBottom: 20 }}>
                  <div style={{ fontSize: 11, color: 'var(--text-3)', fontWeight: 700, letterSpacing: 1.2, textTransform: 'uppercase', marginBottom: 8 }}>Internal Notes</div>
                  <div style={{ fontSize: 13, color: 'var(--text-2)', lineHeight: 1.6, background: 'var(--surface-2)', borderRadius: 9, padding: '12px 14px', borderLeft: '3px solid #bf1e2e' }}>
                    {selected.internal_notes}
                  </div>
                </div>
              )}

              {/* Quick actions */}
              {!editing && (
                <div style={{ marginTop: 24, display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1.5, textTransform: 'uppercase', color: 'var(--text-3)', marginBottom: 4 }}>Quick Actions</div>
                  <button
                    style={{ padding: '10px 14px', borderRadius: 8, background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text-2)', fontSize: 13, fontWeight: 600, cursor: 'pointer', textAlign: 'left', fontFamily: 'Barlow, sans-serif' }}
                    onClick={() => openEdit(selected)}
                  >
                    ✏️ Edit Institution Details
                  </button>
                  <button
                    style={{ padding: '10px 14px', borderRadius: 8, background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text-2)', fontSize: 13, fontWeight: 600, cursor: 'pointer', textAlign: 'left', fontFamily: 'Barlow, sans-serif' }}
                    onClick={handleRenew}
                  >
                    🔄 Renew License (+1 Year)
                  </button>
                  <button
                    style={{ padding: '10px 14px', borderRadius: 8, background: selected.status === 'active' ? 'rgba(191,30,46,0.08)' : 'rgba(15,168,109,0.08)', border: `1px solid ${selected.status === 'active' ? 'rgba(191,30,46,0.3)' : 'rgba(15,168,109,0.3)'}`, color: selected.status === 'active' ? '#bf1e2e' : '#0fa86d', fontSize: 13, fontWeight: 600, cursor: 'pointer', textAlign: 'left', fontFamily: 'Barlow, sans-serif' }}
                    onClick={handleToggleSuspend}
                  >
                    {selected.status === 'active' ? '⏸ Suspend Institution' : '▶ Reactivate Institution'}
                  </button>
                </div>
              )}

              {/* Inline edit form */}
              {editing && editForm && (
                <div style={{ marginTop: 24 }}>
                  <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1.5, textTransform: 'uppercase', color: 'var(--text-3)', marginBottom: 14 }}>Edit Institution</div>
                  {[
                    { label: 'Institution Name *', key: 'name', type: 'text' },
                    { label: 'Contact Name', key: 'contact_name', type: 'text' },
                    { label: 'Contact Email', key: 'contact_email', type: 'email' },
                    { label: 'Province / State', key: 'state_province', type: 'text' },
                    { label: 'License Fee', key: 'license_fee', type: 'number' },
                    { label: 'Max Athletes', key: 'max_athletes', type: 'number' },
                    { label: 'License Start Date', key: 'license_start_date', type: 'date' },
                    { label: 'License End Date', key: 'license_end_date', type: 'date' },
                  ].map(({ label, key, type }) => (
                    <div key={key} style={{ marginBottom: 12 }}>
                      <label style={labelStyle}>{label}</label>
                      <input
                        style={inputStyle}
                        type={type}
                        value={editForm[key]}
                        onChange={e => setEditForm((f: any) => ({ ...f, [key]: e.target.value }))}
                      />
                    </div>
                  ))}
                  {[
                    { label: 'Type', key: 'type', options: INST_TYPES },
                    { label: 'License Tier', key: 'license_tier', options: ['', 'starter', 'pro', 'elite'] },
                    { label: 'Currency', key: 'currency', options: ['CAD', 'USD'] },
                    { label: 'Country', key: 'country', options: ['Canada', 'USA'] },
                    { label: 'Status', key: 'status', options: ['active', 'pending', 'expired', 'suspended'] },
                  ].map(({ label, key, options }) => (
                    <div key={key} style={{ marginBottom: 12 }}>
                      <label style={labelStyle}>{label}</label>
                      <select style={{ ...inputStyle, cursor: 'pointer' }} value={editForm[key]} onChange={e => setEditForm((f: any) => ({ ...f, [key]: e.target.value }))}>
                        {options.map(o => <option key={o} value={o}>{o || 'Select'}</option>)}
                      </select>
                    </div>
                  ))}
                  <div style={{ marginBottom: 16 }}>
                    <label style={labelStyle}>Internal Notes (BTHD only)</label>
                    <textarea style={{ ...inputStyle, height: 70, resize: 'vertical' }} value={editForm.internal_notes} onChange={e => setEditForm((f: any) => ({ ...f, internal_notes: e.target.value }))} />
                  </div>
                  {editError && <div style={{ color: '#bf1e2e', fontSize: 12, marginBottom: 10 }}>{editError}</div>}
                  <div style={{ display: 'flex', gap: 10 }}>
                    <button className="btn btn-primary" onClick={handleEditSave} disabled={editSaving} style={{ flex: 1 }}>
                      {editSaving ? 'Saving...' : 'Save Changes'}
                    </button>
                    <button onClick={() => setEditing(false)} style={{ padding: '9px 16px', borderRadius: 8, background: 'transparent', border: '1px solid var(--border)', color: 'var(--text-3)', fontSize: 13, cursor: 'pointer', fontFamily: 'Barlow, sans-serif' }}>
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
